import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const val = parts.pop().split(';').shift();
    if (val) return val;
  }
  return localStorage.getItem(name) || '';
}

const getFormattedToday = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const calculateTimestamps = (filter, customFrom, customTo) => {
  const now = new Date();
  let start = new Date();
  let end = new Date();

  if (filter === 'today') {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (filter === 'yesterday') {
    start.setDate(now.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    end.setDate(now.getDate() - 1);
    end.setHours(23, 59, 59, 999);
  } else if (filter === '7days') {
    start.setDate(now.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (filter === 'custom' && customFrom && customTo) {
    start = new Date(`${customFrom}T00:00:00`);
    end = new Date(`${customTo}T23:59:59`);
  } else {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  }

  return {
    StartDate: Math.floor(start.getTime() / 1000),
    EndDate: Math.floor(end.getTime() / 1000),
  };
};

const RetailPulseReport = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState('');
  const [apiKeyError, setApiKeyError] = useState('');
  const [posList, setPosList] = useState([]);
  const [selectedPos, setSelectedPos] = useState('');
  const [dateFilter, setDateFilter] = useState('today'); // 'today' | 'yesterday' | '7days' | 'custom'
  const [customFrom, setCustomFrom] = useState(getFormattedToday());
  const [customTo, setCustomTo] = useState(getFormattedToday());
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [syncedTime, setSyncedTime] = useState('');

  // Initial load
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      const key = await fetchApiKey();
      const posId = await fetchPosList(key);
      if (key) {
        fetchReportData(posId || 14, key, 'today', customFrom, customTo);
      } else {
        setLoading(false);
      }
    };

    initData();
  }, []);

  // 1. Fetch API key
  const fetchApiKey = async () => {
    try {
      const token = getCookie('token');
      const userid = getCookie('userid') || '10002';

      const response = await axios.get(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserApiKeyOperation/SubUser/Get',
        {
          headers: {
            accept: 'text/plain',
            UserId: userid,
            'X-Authorization': token,
          },
        }
      );

      if (
        response.data &&
        response.data.isresponse &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        const activeKeyObj = response.data.data.find((item) => item.active && item.apikey) || response.data.data[0];
        if (activeKeyObj && activeKeyObj.apikey) {
          setApiKey(activeKeyObj.apikey);
          setApiKeyError('');
          return activeKeyObj.apikey;
        }
      }

      setApiKey('');
      setApiKeyError('API key not found');
      return null;
    } catch (err) {
      console.error('Error fetching API Key:', err);
      setApiKey('');
      setApiKeyError('API key not found');
      return null;
    }
  };

  // 2. Fetch POS Connected List
  const fetchPosList = async (currentApiKey) => {
    try {
      const key = currentApiKey || apiKey;
      const userid = getCookie('userid') || '10002';

      if (!key) {
        setPosList([]);
        return 14;
      }

      const response = await axios.get(
        `https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlReport/SubUser/ConnectedList?apikey=${key}&subuserid=${userid}`,
        {
          headers: {
            accept: 'text/plain',
          },
        }
      );

      const items = response.data?.list || response.data?.data || (Array.isArray(response.data) ? response.data : []);

      if (Array.isArray(items) && items.length > 0) {
        setPosList(items);
        const firstPos = items[0];
        const posId = firstPos.posid || firstPos.id || firstPos.value || 14;
        setSelectedPos(posId);
        return posId;
      }

      setPosList([]);
      setSelectedPos(14);
      return 14;
    } catch (err) {
      console.error('Error fetching connected POS list:', err);
      setPosList([]);
      setSelectedPos(14);
      return 14;
    }
  };

  // 3. Fetch Direct Report API
  const fetchReportData = async (
    posIdToUse = selectedPos,
    keyToUse = apiKey,
    filterToUse = dateFilter,
    fromToUse = customFrom,
    toToUse = customTo
  ) => {
    setLoading(true);
    setErrorMsg('');
    setApiKeyError('');

    let currentKey = keyToUse || apiKey;
    if (!currentKey) {
      currentKey = await fetchApiKey();
    }

    if (!currentKey) {
      setLoading(false);
      setApiKeyError('API key not found');
      setReportData(null);
      return;
    }

    const posId = posIdToUse || selectedPos || 14;

    try {
      const userid = getCookie('userid') || '10002';
      const { StartDate, EndDate } = calculateTimestamps(filterToUse, fromToUse, toToUse);

      const payload = {
        posid: Number(posId),
        subuserid: Number(userid),
        msg: JSON.stringify({
          Action: 'Dashboard',
          Msg: JSON.stringify({
            StartDate: StartDate,
            EndDate: EndDate,
            TopSelling: 15,
            RecentInvoice: 15,
            IsAllTime: false,
          }),
        }),
        apikey: currentKey,
      };

      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlReport/SubUser/Direct/Report',
        payload,
        {
          headers: {
            accept: 'text/plain',
            'Content-Type': 'application/json-patch+json',
          },
        }
      );

      if (response.data && response.data.isresponse) {
        let parsed = null;
        if (typeof response.data.data === 'string') {
          parsed = JSON.parse(response.data.data);
        } else {
          parsed = response.data.data;
        }

        if (parsed && parsed.IsResponse === false) {
          setErrorMsg(parsed.Error || 'Error fetching report.');
          setReportData(null);
        } else {
          setReportData(parsed);
          setSyncedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        }
      } else {
        setErrorMsg(response.data?.errormsg || 'Failed to fetch report data.');
        setReportData(null);
      }
    } catch (err) {
      console.error('Error calling Direct Report API:', err);
      setErrorMsg('An error occurred while fetching the report.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePosChange = (e) => {
    const newPosId = e.target.value;
    setSelectedPos(newPosId);
    fetchReportData(newPosId, apiKey, dateFilter, customFrom, customTo);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    fetchReportData(selectedPos, apiKey, filter, customFrom, customTo);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    fetchReportData(selectedPos, apiKey, 'custom', customFrom, customTo);
  };

  // Helper values
  const totalInvoiceObj = reportData?.TotalInvoice?.[0] || { InvoiceCount: 0, TotalAmount: 0 };
  const paymentModes = reportData?.PaymentModeWiseSales || [];
  const topSelling = reportData?.TopSelling || [];
  const recentInvoices = reportData?.RecentInvoice || [];
  const categoryWise = reportData?.CategoryWise || [];
  const hourlyData = reportData?.HourlyData || [];

  const cashAmount = paymentModes.find(p => p.Report?.toLowerCase() === 'cash')?.TotalAmount || 0;
  const upiAmount = paymentModes.find(p => p.Report?.toLowerCase() === 'upi' || p.Report?.toLowerCase() === 'bhim upi')?.TotalAmount || 0;
  const otherAmount = paymentModes.filter(p => !['cash', 'upi', 'bhim upi'].includes(p.Report?.toLowerCase())).reduce((acc, curr) => acc + (curr.TotalAmount || 0), 0);

  return (
    <div style={styles.body}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.brand}>
          <i className="fas fa-chart-pie" style={{ color: '#6366f1', fontSize: '1.4rem' }}></i>
          <span>RetailPulse POS</span>
        </div>
        <ul style={styles.navMenu}>
          <li style={{ ...styles.navItem, ...styles.navItemActive }}>
            <a href="#" onClick={(e) => e.preventDefault()} style={styles.navLinkActive}>
              <i className="fas fa-tachometer-alt"></i> Overview
            </a>
          </li>
          <li style={styles.navItem}>
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/'); }} style={styles.navLink}>
              <i className="fas fa-store"></i> Merchant Panel
            </a>
          </li>
          <li style={styles.navItem}>
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/select-option'); }} style={styles.navLink}>
              <i className="fas fa-th-large"></i> Portal Options
            </a>
          </li>
          <li style={styles.navItem}>
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/third-party-report'); }} style={styles.navLink}>
              <i className="fas fa-file-invoice"></i> Standard Report
            </a>
          </li>
        </ul>
        <div style={styles.sidebarFooter}>
          <div style={styles.storeBadge}>
            <div style={styles.storeAvatar}><i className="fas fa-store"></i></div>
            <div style={styles.storeInfo}>
              <h4 style={{ margin: 0, fontSize: '0.85rem', color: '#fff' }}>POS Terminal</h4>
              <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>Connected</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Wrapper */}
      <div style={styles.mainWrapper}>
        {/* Topbar */}
        <header style={styles.topbar}>
          <div style={styles.topbarLeft}>
            <span style={{ fontWeight: '600', color: '#64748b', fontSize: '0.9rem' }}>Select POS:</span>
            <select 
              value={selectedPos} 
              onChange={handlePosChange}
              style={styles.branchSelector}
            >
              {posList.length > 0 ? (
                posList.map((pos, idx) => {
                  const id = pos.posid || pos.id || pos.value;
                  const name = pos.posname || pos.name || pos.pos_name || pos.value || `POS ${id}`;
                  return <option key={idx} value={id}>{name} (ID: {id})</option>;
                })
              ) : (
                <option value="14">POS ID: 14</option>
              )}
            </select>
            {syncedTime && (
              <span style={styles.liveStatus}>
                <span style={styles.liveDot}></span>
                Synced at {syncedTime}
              </span>
            )}
          </div>

          <div style={styles.topbarRight}>
            <div style={styles.dateFilterGroup}>
              <button 
                style={dateFilter === 'today' ? styles.dateBtnActive : styles.dateBtn}
                onClick={() => handleDateFilterChange('today')}
              >
                Today
              </button>
              <button 
                style={dateFilter === 'yesterday' ? styles.dateBtnActive : styles.dateBtn}
                onClick={() => handleDateFilterChange('yesterday')}
              >
                Yesterday
              </button>
              <button 
                style={dateFilter === '7days' ? styles.dateBtnActive : styles.dateBtn}
                onClick={() => handleDateFilterChange('7days')}
              >
                7 Days
              </button>
              <button 
                style={dateFilter === 'custom' ? styles.dateBtnActive : styles.dateBtn}
                onClick={() => setDateFilter('custom')}
              >
                Custom
              </button>
            </div>

            {dateFilter === 'custom' && (
              <form onSubmit={handleCustomSubmit} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input 
                  type="date" 
                  value={customFrom} 
                  onChange={(e) => setCustomFrom(e.target.value)} 
                  style={styles.customDateInput}
                  required 
                />
                <input 
                  type="date" 
                  value={customTo} 
                  onChange={(e) => setCustomTo(e.target.value)} 
                  style={styles.customDateInput}
                  required 
                />
                <button type="submit" style={styles.applyBtn}>Apply</button>
              </form>
            )}

            <button 
              style={styles.btnRefresh} 
              onClick={() => fetchReportData(selectedPos, apiKey, dateFilter, customFrom, customTo)}
              disabled={loading}
            >
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
              Refresh
            </button>
          </div>
        </header>

        {/* Dashboard Body */}
        <main style={styles.dashboardBody}>
          {apiKeyError && (
            <div style={styles.alertDanger}>
              <i className="fas fa-exclamation-triangle mr-2"></i> {apiKeyError}
            </div>
          )}

          {!apiKeyError && errorMsg && (
            <div style={styles.alertWarning}>
              <i className="fas fa-exclamation-triangle mr-2"></i> {errorMsg}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '2.5rem', color: '#4f46e5' }}></i>
              <p style={{ marginTop: '16px', color: '#64748b', fontWeight: '600' }}>Fetching Live Sales Report...</p>
            </div>
          ) : reportData ? (
            <>
              {/* Metrics Grid */}
              <div style={styles.metricsGrid}>
                <div style={{ ...styles.metricCard, borderLeft: '4px solid #4f46e5' }}>
                  <div style={styles.cardTop}>
                    <span>Total Sales</span>
                    <div style={{ ...styles.cardIcon, background: '#eef2ff', color: '#4f46e5' }}>
                      <i className="fas fa-rupee-sign"></i>
                    </div>
                  </div>
                  <div style={styles.metricVal}>
                    ₹{(totalInvoiceObj.TotalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={styles.metricSub}>
                    <span>Invoices</span>
                    <strong>{totalInvoiceObj.InvoiceCount || 0} Orders</strong>
                  </div>
                </div>

                <div style={{ ...styles.metricCard, borderLeft: '4px solid #10b981' }}>
                  <div style={styles.cardTop}>
                    <span>Cash Collected</span>
                    <div style={{ ...styles.cardIcon, background: '#ecfdf5', color: '#10b981' }}>
                      <i className="fas fa-money-bill-wave"></i>
                    </div>
                  </div>
                  <div style={styles.metricVal}>
                    ₹{cashAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={styles.metricSub}>
                    <span>Mode</span>
                    <strong>Cash Payment</strong>
                  </div>
                </div>

                <div style={{ ...styles.metricCard, borderLeft: '4px solid #0284c7' }}>
                  <div style={styles.cardTop}>
                    <span>UPI / QR</span>
                    <div style={{ ...styles.cardIcon, background: '#f0f9ff', color: '#0284c7' }}>
                      <i className="fas fa-qrcode"></i>
                    </div>
                  </div>
                  <div style={styles.metricVal}>
                    ₹{upiAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={styles.metricSub}>
                    <span>Digital</span>
                    <strong>Instant UPI</strong>
                  </div>
                </div>

                <div style={{ ...styles.metricCard, borderLeft: '4px solid #8b5cf6' }}>
                  <div style={styles.cardTop}>
                    <span>Other Modes</span>
                    <div style={{ ...styles.cardIcon, background: '#f5f3ff', color: '#8b5cf6' }}>
                      <i className="fas fa-credit-card"></i>
                    </div>
                  </div>
                  <div style={styles.metricVal}>
                    ₹{otherAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={styles.metricSub}>
                    <span>Other</span>
                    <strong>Cards & Wallet</strong>
                  </div>
                </div>
              </div>

              {/* Payment Mode Wise Summary */}
              <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                    <i className="fas fa-wallet mr-2" style={{ color: '#4f46e5' }}></i> Payment Mode Breakdown
                  </h3>
                </div>
                <div style={{ padding: '16px' }}>
                  <div style={styles.paymentSummaryRow}>
                    {paymentModes.map((pm, idx) => (
                      <div key={idx} style={styles.payCard}>
                        <div style={styles.payHeader}>
                          <span>{pm.Report || 'Default'}</span>
                          <span style={{ color: '#10b981' }}>₹{pm.TotalAmount?.toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {pm.InvoiceCount} Invoices
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Selling & Categories Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
                <div style={styles.tableCard}>
                  <div style={styles.tableToolbar}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                      <i className="fas fa-fire text-danger mr-2"></i> Top Selling Items
                    </h3>
                  </div>
                  <div style={{ overflowX: 'auto', maxHeight: '350px' }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Item Name</th>
                          <th style={styles.th}>Category</th>
                          <th style={styles.th}>Qty</th>
                          <th style={styles.th}>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topSelling.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ ...styles.td, fontWeight: '600' }}>{item.item_name}</td>
                            <td style={styles.td}>{item.categoryname}</td>
                            <td style={styles.td}><span style={styles.badgePrimary}>{item.TotalQty}</span></td>
                            <td style={{ ...styles.td, fontWeight: '700', color: '#10b981' }}>₹{item.TotalAmount?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={styles.tableCard}>
                  <div style={styles.tableToolbar}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                      <i className="fas fa-tags text-warning mr-2"></i> Category Sales
                    </h3>
                  </div>
                  <div style={{ overflowX: 'auto', maxHeight: '350px' }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Category</th>
                          <th style={styles.th}>Qty</th>
                          <th style={styles.th}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryWise.map((cat, idx) => (
                          <tr key={idx}>
                            <td style={{ ...styles.td, fontWeight: '600' }}>{cat.categoryname}</td>
                            <td style={styles.td}>{cat.TotalQty}</td>
                            <td style={{ ...styles.td, fontWeight: '700', color: '#10b981' }}>₹{cat.TotalAmount?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Recent Invoices Table */}
              <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                    <i className="fas fa-receipt mr-2" style={{ color: '#4f46e5' }}></i> Recent Transactions
                  </h3>
                </div>
                <div style={{ overflowX: 'auto', maxHeight: '400px' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Invoice #</th>
                        <th style={styles.th}>Date & Time</th>
                        <th style={styles.th}>Order Type</th>
                        <th style={styles.th}>Payment Mode</th>
                        <th style={styles.th}>Grand Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentInvoices.map((inv, idx) => (
                        <tr key={idx}>
                          <td style={{ ...styles.td, fontWeight: '700' }}>#{inv.id || inv.bill_id}</td>
                          <td style={styles.td}>{inv.payment_date || inv.Date?.split('T')[0]}</td>
                          <td style={styles.td}><span style={styles.badgeSuccess}>{inv.ordertype || 'Confirmed'}</span></td>
                          <td style={styles.td}><span style={styles.badgeInfo}>{inv.remarks || 'Cash'}</span></td>
                          <td style={{ ...styles.td, fontWeight: '700', color: '#10b981' }}>₹{inv.grand_total?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <h4>No Report Data Available. Please select a POS.</h4>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// Inline Scoped Styles matching ai_studio_code (12).html
const styles = {
  body: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    color: '#1e293b',
    fontFamily: "'Inter', sans-serif",
  },
  sidebar: {
    width: '260px',
    backgroundColor: '#0f172a',
    color: '#94a3b8',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 100,
  },
  brand: {
    padding: '22px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#ffffff',
    fontSize: '1.2rem',
    fontWeight: '700',
    borderBottom: '1px solid #1e293b',
  },
  navMenu: {
    listStyle: 'none',
    padding: '16px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
  },
  navItem: {
    borderRadius: '8px',
  },
  navItemActive: {
    backgroundColor: '#4f46e5',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    color: '#94a3b8',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  navLinkActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    color: '#ffffff',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  sidebarFooter: {
    padding: '16px',
    borderTop: '1px solid #1e293b',
  },
  storeBadge: {
    background: '#1e293b',
    borderRadius: '8px',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  storeAvatar: {
    width: '36px',
    height: '36px',
    background: '#334155',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  storeInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  mainWrapper: {
    flex: 1,
    marginLeft: '260px',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  topbar: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  topbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  topbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  branchSelector: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    padding: '8px 14px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
  },
  liveStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#10b981',
    background: '#ecfdf5',
    padding: '4px 10px',
    borderRadius: '20px',
  },
  liveDot: {
    width: '7px',
    height: '7px',
    backgroundColor: '#10b981',
    borderRadius: '50%',
  },
  dateFilterGroup: {
    display: 'flex',
    background: '#f8fafc',
    padding: '3px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  dateBtn: {
    background: 'none',
    border: 'none',
    padding: '6px 12px',
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#64748b',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  dateBtnActive: {
    background: '#ffffff',
    border: 'none',
    padding: '6px 12px',
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#4f46e5',
    borderRadius: '6px',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  customDateInput: {
    padding: '4px 8px',
    fontSize: '0.8rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
  },
  applyBtn: {
    padding: '5px 10px',
    fontSize: '0.8rem',
    borderRadius: '6px',
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
  },
  btnRefresh: {
    background: '#eef2ff',
    color: '#4f46e5',
    border: '1px solid #c7d2fe',
    padding: '7px 12px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  dashboardBody: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  alertDanger: {
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    border: '1px solid #fca5a5',
    padding: '14px 18px',
    borderRadius: '8px',
    fontWeight: '600',
  },
  alertWarning: {
    backgroundColor: '#fffbeb',
    color: '#f59e0b',
    border: '1px solid #fde68a',
    padding: '14px 18px',
    borderRadius: '8px',
    fontWeight: '600',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: '18px',
  },
  metricCard: {
    background: '#ffffff',
    padding: '18px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricVal: {
    fontSize: '1.55rem',
    fontWeight: '700',
    color: '#1e293b',
  },
  metricSub: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.78rem',
    color: '#64748b',
    borderTop: '1px dashed #e2e8f0',
    paddingTop: '8px',
  },
  tableCard: {
    background: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  tableToolbar: {
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paymentSummaryRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  payCard: {
    background: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  payHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: '600',
    fontSize: '0.9rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
    fontSize: '0.88rem',
  },
  th: {
    padding: '12px 18px',
    fontWeight: '600',
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '14px 18px',
    borderBottom: '1px solid #e2e8f0',
    color: '#1e293b',
  },
  badgePrimary: {
    background: '#eef2ff',
    color: '#4f46e5',
    padding: '3px 8px',
    borderRadius: '12px',
    fontWeight: '600',
    fontSize: '0.75rem',
  },
  badgeSuccess: {
    background: '#ecfdf5',
    color: '#10b981',
    padding: '3px 8px',
    borderRadius: '12px',
    fontWeight: '600',
    fontSize: '0.75rem',
  },
  badgeInfo: {
    background: '#f0f9ff',
    color: '#0284c7',
    padding: '3px 8px',
    borderRadius: '12px',
    fontWeight: '600',
    fontSize: '0.75rem',
  },
};

export default RetailPulseReport;
